import { useState, useMemo } from 'react';
   import { WarehouseOrder, WarehouseOrderItem } from '../types';

   type SortKey = 'id' | 'customer_name' | 'status';
   type SortOrder = 'asc' | 'desc';

   interface SortConfig {
     key: SortKey;
     order: SortOrder;
   }

   const useWarehouseOrdersTable = (orders: WarehouseOrder[], page: number, perPage: number = 200) => {
     const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'id', order: 'desc' });
     const [searchTerm, setSearchTerm] = useState('');

     const handleSort = (key: SortKey) => {
       setSortConfig((prev) => {
         const newOrder = prev.key === key && prev.order === 'asc' ? 'desc' : 'asc';
         return { key, order: newOrder };
       });
     };

     const sortedAndFilteredData = useMemo(() => {
       let data = [...orders];

       if (sortConfig.key) {
         data = data.sort((a, b) => {
           const { key, order } = sortConfig;
           let aValue: string | number = a[key] ?? '';
           let bValue: string | number = b[key] ?? '';

           if (key === 'id') {
             aValue = Number(aValue) || 0;
             bValue = Number(bValue) || 0;
           } else {
             aValue = String(aValue).toLowerCase();
             bValue = String(bValue).toLowerCase();
           }

           if (aValue < bValue) return order === 'asc' ? -1 : 1;
           if (aValue > bValue) return order === 'asc' ? 1 : -1;
           return 0;
         });
       }

       if (searchTerm) {
         const lowerSearchTerm = searchTerm.toLowerCase();
         data = data.filter((order) =>
           String(order.id).includes(lowerSearchTerm) ||
           (order.customer_name?.toLowerCase().includes(lowerSearchTerm) ?? false) ||
           order.status.toLowerCase().includes(lowerSearchTerm) ||
           order.items.some((item) =>
             item.product_name.toLowerCase().includes(lowerSearchTerm) ||
             (item.sku?.toLowerCase().includes(lowerSearchTerm) ?? false) ||
             (item.gtin?.toLowerCase().includes(lowerSearchTerm) ?? false)
           )
         );
       }

       return data;
     }, [orders, sortConfig.key, sortConfig.order, searchTerm]);

     // Apply client-side pagination
     const displayedData = useMemo(() => {
       const start = (page - 1) * perPage;
       return sortedAndFilteredData.slice(start, start + perPage);
     }, [sortedAndFilteredData, page, perPage]);

     const totalPages = Math.ceil(sortedAndFilteredData.length / perPage);

     return {
       displayedData,
       totalPages,
       sortConfig,
       searchTerm,
       setSearchTerm,
       handleSort,
     };
   };

   export default useWarehouseOrdersTable;